import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Paper,
    Stack,
    Typography
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { OpenModalHookType } from '@hooks/useOpenModal';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Calculator from '@components/Utilities/Calculator';
import CashRegisterServ from '@api/CashRegister/CashRegisterServ';
import { CashRegister } from '@api/CashRegister/CashRegister';
import { getCashRegisterDate } from '@utils/CashRegisters';
import { showAlertError } from '@utils/Alerts/Error';
import { format } from 'date-fns';
import * as locales from 'date-fns/locale';
import { currencyFormatter } from '@utils/Numbers';

interface modalOpenCashRegisterProps {
    modalOpenCashRegister: OpenModalHookType;
    onOpenCashRegister: (amount: number) => void;
    /**
     * Modo CANDADO (2026-08-18, pedido de Irving: "necesito que siempre inicien
     * caja"). Sin caja abierta no se puede operar el POS: el modal deja de
     * cerrarse con la X, con Escape o clicando fuera. Antes se descartaba y se
     * podía armar un carrito completo, y el bloqueo llegaba hasta el final —
     * o peor: el cobro de mesa ni siquiera lo verificaba, y ese dinero caía
     * fuera de toda caja ("vendió sin caja abierta" en el panel de Cajas).
     * La única salida sin abrir caja es `onExit`.
     */
    mandatory?: boolean;
    /** Salir del POS sin abrir caja (solo en modo candado). */
    onExit?: () => void;
    /** Sucursal en operación: define qué cajas rezagadas bloquean. */
    branchOfficeId?: string;
}

export const ModalOpenCashRegister: React.FunctionComponent<modalOpenCashRegisterProps> = (props: any) => {
    const { modalOpenCashRegister, onOpenCashRegister, mandatory, onExit, branchOfficeId } = props;
    const [amount, setAmount] = useState<number>(0);
    // Cajas de días anteriores que siguen abiertas en esta sucursal. Mientras
    // haya una, no se puede abrir la de hoy (ver `getBlockingCashRegisters`).
    const [rezagadas, setRezagadas] = useState<CashRegister[]>([]);
    const [cargando, setCargando] = useState<boolean>(false);
    const [cerrando, setCerrando] = useState<string | undefined>(undefined);
    const [efectivo, setEfectivo] = useState<number>(0);

    const cargarRezagadas = useCallback(async () => {
        setCargando(true);
        try {
            // La consulta filtra por estado y sucursal; el corte por día lo hace
            // el servicio. Son pocas: no hay riesgo de costo aquí.
            const pendientes = await CashRegisterServ.getOpenCashRegistersFromPreviousDays(branchOfficeId);
            setRezagadas(pendientes);
        } catch (error) {
            // Si no se pueden leer, NO se bloquea: dejar a alguien sin vender
            // por una consulta fallida es peor que una caja rezagada de más.
            console.error('No se pudieron leer las cajas rezagadas', error);
            setRezagadas([]);
        }
        setCargando(false);
    }, [branchOfficeId]);

    useEffect(() => {
        if (modalOpenCashRegister.modalOpen) {
            setCerrando(undefined);
            void cargarRezagadas();
        }
    }, [modalOpenCashRegister.modalOpen, cargarRezagadas]);

    const handleClose = () => {
        setAmount(0);
        modalOpenCashRegister.toggle();
    };

    const onDone = () => {
        handleClose();
        onOpenCashRegister(amount);
    };

    const onCerrarRezagada = async (register: CashRegister, countedCash?: number) => {
        setCargando(true);
        try {
            await CashRegisterServ.closeStaleCashRegister(register, { countedCash });
            setCerrando(undefined);
            setEfectivo(0);
            await cargarRezagadas();
        } catch (error: any) {
            showAlertError(error);
            setCargando(false);
        }
    };

    const fechaDe = (register: CashRegister) =>
        format(getCashRegisterDate(register.date), "EEEE d 'de' MMMM, HH:mm", { locale: locales.es });

    const hayRezagadas = rezagadas.length > 0;

    return (
        <Dialog
            PaperProps={{
                style: { borderRadius: 15, padding: 4 }
            }}
            open={modalOpenCashRegister.modalOpen}
            // En modo candado no hay forma de descartarlo: ni backdrop ni Escape
            onClose={mandatory ? undefined : handleClose}
            disableEscapeKeyDown={mandatory}
            aria-labelledby="modal-credit-card-calculator-title"
            aria-describedby="modal-credit-card-calculator-description"
            fullWidth
            maxWidth="xs"
        >
            <DialogTitle id="modal-credit-card-calculator-title">
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    {mandatory ? (
                        <Typography variant="h6" fontWeight={600}>
                            Abre tu caja para vender
                        </Typography>
                    ) : (
                        <span />
                    )}
                    {!mandatory && (
                        <IconButton color="secondary" onClick={handleClose}>
                            <CloseIcon />
                        </IconButton>
                    )}
                </Stack>
            </DialogTitle>
            <DialogContent>
                {hayRezagadas ? (
                    <Stack spacing={2}>
                        <Alert severity="warning">
                            {rezagadas.length === 1
                                ? 'Quedó una caja sin cerrar de otro día. Ciérrala para poder abrir la de hoy.'
                                : `Quedaron ${rezagadas.length} cajas sin cerrar de otros días. Ciérralas para poder abrir la de hoy.`}
                        </Alert>
                        {rezagadas.map((register) => (
                            <Paper key={register.id} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                <Stack spacing={1}>
                                    <Typography fontWeight={600} textTransform="capitalize">
                                        {fechaDe(register)}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {`Abierta por ${register.username || 'alguien de la sucursal'} · fondo ${currencyFormatter((
                                            register.initialCash || 0
                                        ))}`}
                                    </Typography>
                                    {cerrando === register.id ? (
                                        <Stack spacing={1} alignItems="center">
                                            <Divider flexItem />
                                            <Typography variant="body2">¿Cuánto efectivo hay en esta caja?</Typography>
                                            <Calculator onChangeValue={setEfectivo} />
                                            <Stack direction="row" spacing={1} width="100%">
                                                <Button
                                                    fullWidth
                                                    color="inherit"
                                                    disabled={cargando}
                                                    onClick={() => setCerrando(undefined)}
                                                >
                                                    Cancelar
                                                </Button>
                                                <Button
                                                    fullWidth
                                                    variant="contained"
                                                    disabled={cargando}
                                                    onClick={() => onCerrarRezagada(register, efectivo)}
                                                >
                                                    {`Cerrar con ${currencyFormatter(efectivo)}`}
                                                </Button>
                                            </Stack>
                                        </Stack>
                                    ) : (
                                        <Stack direction="row" spacing={1}>
                                            <Button
                                                fullWidth
                                                variant="contained"
                                                disabled={cargando}
                                                onClick={() => {
                                                    setEfectivo(0);
                                                    setCerrando(register.id);
                                                }}
                                            >
                                                Contar y cerrar
                                            </Button>
                                            <Button
                                                fullWidth
                                                color="inherit"
                                                disabled={cargando}
                                                onClick={() => onCerrarRezagada(register)}
                                            >
                                                Cerrar sin contar
                                            </Button>
                                        </Stack>
                                    )}
                                </Stack>
                            </Paper>
                        ))}
                        <Typography variant="caption" color="text.secondary">
                            Esta caja es de otro turno, así que lo que captures aquí NO se compara contra lo esperado ni
                            genera una diferencia a nombre de nadie: solo deja registrado con cuánto se cerró.
                        </Typography>
                    </Stack>
                ) : (
                    <Stack alignItems="center" spacing={2}>
                        {mandatory && (
                            <Alert severity="info" sx={{ width: '100%' }}>
                                Todas las ventas del turno se registran en esta caja. Sin ella no se puede tomar
                                pedidos.
                            </Alert>
                        )}
                        <Typography>Monto inicial en caja</Typography>
                        <Calculator onChangeValue={setAmount} />
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                <Stack width="100%" spacing={1}>
                    {!hayRezagadas && (
                        <Button disabled={amount === 0} fullWidth onClick={onDone} color="success" variant="contained">
                            Abrir caja
                        </Button>
                    )}
                    {mandatory && onExit && (
                        <Button fullWidth color="inherit" onClick={onExit}>
                            Salir del punto de venta
                        </Button>
                    )}
                </Stack>
            </DialogActions>
        </Dialog>
    );
};
